import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { Mail } from "lucide-react"
import { PortalApiError, portalApi } from "@/lib/portal-api"
import { track } from "@/lib/posthog"
import { Logo } from "@/components/Logo"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"

function validateEmail(email: string): string | null {
  if (!email) return "Email is required"
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email)) return "Invalid email format"
  return null
}

export function PortalLogin() {
  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState("")
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null)
  const [now, setNow] = useState(0)

  useEffect(() => {
    if (!rateLimitedUntil) return

    const interval = window.setInterval(() => {
      const currentTime = Date.now()
      setNow(currentTime)
      if (currentTime >= rateLimitedUntil) {
        setRateLimitedUntil(null)
      }
    }, 1000)

    return () => window.clearInterval(interval)
  }, [rateLimitedUntil])

  const remainingSeconds = rateLimitedUntil
    ? Math.max(Math.ceil((rateLimitedUntil - now) / 1000), 0)
    : 0

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError("")

    if (remainingSeconds > 0) {
      setError(`Please wait ${remainingSeconds} seconds before requesting another link.`)
      return
    }

    const emailErr = validateEmail(email)
    setFieldError(emailErr)
    if (emailErr) return

    setLoading(true)

    try {
      await portalApi("/api/portal/request-access", {
        method: "POST",
        body: JSON.stringify({ email }),
      })

      setSent(true)
      track("portal_login_requested")
    } catch (err) {
      if (err instanceof PortalApiError && err.statusCode === 429) {
        const retryAfter = err.retryAfterSeconds || 60
        const requestTime = Date.now()
        setNow(requestTime)
        setRateLimitedUntil(requestTime + retryAfter * 1000)
        setError(`Too many requests. Try again in ${retryAfter} seconds.`)
      } else {
        setError(err instanceof Error ? err.message : "Failed to send magic link")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-background flex min-h-screen items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Card className="py-6">
          <CardHeader className="pb-2 text-center">
            <div className="mb-2 flex justify-center">
              <Logo className="h-14" bgFill="var(--card)" />
            </div>
            <CardTitle className="text-2xl font-bold">Client Portal</CardTitle>
            <CardDescription>Access your billing information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {sent ? (
              <Alert>
                <AlertDescription>
                  If an account exists for this email, a magic link has been sent. Please check your
                  inbox.
                </AlertDescription>
              </Alert>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Billing Email</Label>
                  <div className="relative">
                    <Mail className="text-muted-foreground absolute top-3 left-3 h-4 w-4" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value)
                        if (fieldError) {
                          setFieldError(validateEmail(e.target.value))
                        }
                      }}
                      placeholder="you@company.com"
                      className={`pl-9 ${fieldError ? "border-destructive" : ""}`}
                    />
                  </div>
                  {fieldError && <p className="text-destructive text-xs">{fieldError}</p>}
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      {error}
                      {remainingSeconds > 0 ? ` ${remainingSeconds}s remaining.` : ""}
                    </AlertDescription>
                  </Alert>
                )}

                <Button type="submit" className="w-full" disabled={loading || remainingSeconds > 0}>
                  {loading
                    ? "Sending..."
                    : remainingSeconds > 0
                      ? `Try Again In ${remainingSeconds}s`
                      : "Send Magic Link"}
                </Button>
              </form>
            )}

            <p className="text-muted-foreground text-center text-xs">
              Need help? Contact the account owner for access.
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}
